import { createBlock } from './create';
import { mapTableCellLists, tableCellLists } from './table';

// Find a block by id anywhere in the tree (including table cells)
export const findBlock = (blocks, blockId) => {
  for (const block of blocks || []) {
    if (block.id === blockId) return block;
    if (block.children?.length) {
      const found = findBlock(block.children, blockId);
      if (found) return found;
    }
    for (const cell of tableCellLists(block)) {
      if (Array.isArray(cell) && cell.length) {
        const found = findBlock(cell, blockId);
        if (found) return found;
      }
    }
  }
  return null;
};

/** Table that hosts `blockId` in a cell, or null if the block is on the page. */
export const findTableIdForBlock = (blocks, blockId) => {
  for (const block of blocks || []) {
    if (block.type === 'table') {
      for (const cell of tableCellLists(block)) {
        if (Array.isArray(cell) && findBlock(cell, blockId)) return block.id;
      }
    }
    if (block.children?.length) {
      const id = findTableIdForBlock(block.children, blockId);
      if (id) return id;
    }
  }
  return null;
};

// Immutably apply partial updates to one block in the tree
export const updateBlockInTree = (blocks, blockId, updates) => {
  // Identity-preserving: only the edited block and its ancestor chain get new
  // objects. Untouched siblings/subtrees keep their references so memoized
  // renderers skip them — cloning every container per keystroke made typing
  // lag scale with the number of callouts/toggles in the doc.
  let changed = false;
  const next = blocks.map((block) => {
    if (block.id === blockId) {
      changed = true;
      return { ...block, ...updates };
    }
    let current = block;
    if (block.children?.length) {
      const children = updateBlockInTree(block.children, blockId, updates);
      if (children !== block.children) {
        changed = true;
        current = { ...current, children };
      }
    }
    if (current.type === 'table') {
      const mapped = mapTableCellLists(current, (cell) =>
        updateBlockInTree(cell, blockId, updates)
      );
      if (mapped !== current) {
        changed = true;
        current = mapped;
      }
    }
    return current;
  });
  return changed ? next : blocks;
};

// Immutably remove a block from the tree
export const removeBlockFromTree = (blocks, blockId) => {
  const filtered = blocks.filter((block) => block.id !== blockId);
  let changed = filtered.length !== blocks.length;
  const next = filtered.map((block) => {
    let current = block;
    if (block.children?.length) {
      const children = removeBlockFromTree(block.children, blockId);
      if (children !== block.children) {
        changed = true;
        current = { ...current, children };
      }
    }
    if (current.type === 'table') {
      const mapped = mapTableCellLists(current, (cell) => {
        const inner = removeBlockFromTree(cell, blockId);
        if (inner === cell) return cell;
        return inner.length ? inner : [createBlock('text')];
      });
      if (mapped !== current) {
        changed = true;
        current = mapped;
      }
    }
    return current;
  });
  return changed ? next : blocks;
};

// Immutably insert a new block directly after the target block
export const insertBlockAfter = (blocks, targetId, newBlock) => {
  const index = blocks.findIndex((block) => block.id === targetId);
  if (index !== -1) {
    const next = [...blocks];
    next.splice(index + 1, 0, newBlock);
    return next;
  }
  let changed = false;
  const next = blocks.map((block) => {
    let current = block;
    if (block.children?.length) {
      const children = insertBlockAfter(block.children, targetId, newBlock);
      if (children !== block.children) {
        changed = true;
        current = { ...current, children };
      }
    }
    if (current.type === 'table') {
      const mapped = mapTableCellLists(current, (cell) =>
        insertBlockAfter(cell, targetId, newBlock)
      );
      if (mapped !== current) {
        changed = true;
        current = mapped;
      }
    }
    return current;
  });
  return changed ? next : blocks;
};
